import { api } from '@/lib/api';
import { queryKeys } from '@/lib/query-keys';
import { useAuthQuery } from './use-auth-query';
import { useBatchedPaginatedQuery } from './use-batched-paginated-query';
import type { BatchFetcherOpts } from './use-batched-paginated-query';

interface TaskListOpts {
  viewPage: number;
  viewPageSize?: number;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
  /** Comma-separated status filter, e.g. "ASSIGNED,IN_PROGRESS" */
  status?: string;
  /** Task type filter, e.g. "LAND_MONITORING" */
  type?: string;
  search?: string;
}

export function useTasks(opts: TaskListOpts) {
  const viewPageSize = opts.viewPageSize ?? 10;
  return useBatchedPaginatedQuery(
    queryKeys.tasks.all(),
    (fetchOpts: BatchFetcherOpts) =>
      api.getTasks({
        page: fetchOpts.page,
        limit: fetchOpts.limit,
        sortBy: fetchOpts.sortBy as string | undefined,
        sortOrder: fetchOpts.sortOrder as 'asc' | 'desc' | undefined,
        status: fetchOpts.status as string | undefined,
        type: fetchOpts.type as string | undefined,
        search: fetchOpts.search as string | undefined,
      }),
    {
      viewPage: opts.viewPage,
      viewPageSize,
      sortBy: opts.sortBy,
      sortOrder: opts.sortOrder,
      filters: { status: opts.status, type: opts.type, search: opts.search },
      staleTime: 1000 * 60,
    },
  );
}

export function useOpenTasks(opts: Omit<TaskListOpts, 'status' | 'type'>) {
  return useTasks({ ...opts, status: 'ASSIGNED,IN_PROGRESS', type: 'LAND_MONITORING' });
}

export function useCompletedTasks(opts: Omit<TaskListOpts, 'status' | 'type'>) {
  return useTasks({ ...opts, status: 'COMPLETED', type: 'LAND_MONITORING' });
}

export function useTask(id: string) {
  return useAuthQuery({
    queryKey: queryKeys.tasks.detail(id),
    queryFn: () => api.getTask(id),
    enabled: !!id,
    staleTime: 1000 * 60,
  });
}

export function useUserProfile(userId: string | undefined) {
  return useAuthQuery({
    queryKey: queryKeys.user.byId(userId ?? ''),
    queryFn: () => api.getUserProfile(userId!),
    enabled: !!userId,
    staleTime: 1000 * 60 * 60,
  });
}
